import { Component, OnInit, computed, inject, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { SessionStore } from '../../../core/auth/session.store';
import { TranslationService } from '../../../core/i18n/translation.service';
import { IssueDto } from '../../issues/models/issue.models';
import { AuditUnlockInboxDto, IssueNotificationDto } from '../models/audit.models';
import { AuditsApi } from '../services/audits.api';

@Component({
  selector: 'app-audit-unlock-notifications',
  templateUrl: './audit-unlock-notifications.component.html',
  styleUrl: './audit-unlock-notifications.component.css',
})
export class AuditUnlockNotificationsComponent implements OnInit {
  readonly issueRequested = output<IssueDto>();
  protected readonly i18n = inject(TranslationService);
  protected readonly role = inject(SessionStore).session;
  private readonly api = inject(AuditsApi);
  private readonly router = inject(Router);

  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  protected readonly busyId = signal('');
  protected readonly selectedPlanId = signal('');
  protected readonly reason = signal('');
  protected readonly inbox = signal<AuditUnlockInboxDto>({ incoming: [], outgoing: [], eligible_executions: [], new_issues: [] });
  protected readonly pendingCount = computed(() =>
    this.inbox().incoming.filter(item => item.status === 'pending').length + this.inbox().new_issues.length,
  );

  ngOnInit(): void {
    void this.load();
  }

  protected toggle(): void {
    this.open.update(value => !value);
    if (this.open()) void this.load();
  }

  protected close(): void {
    this.open.set(false);
  }

  protected selectPlan(planId: string): void {
    this.selectedPlanId.set(this.selectedPlanId() === planId ? '' : planId);
    this.reason.set('');
    this.error.set('');
  }

  protected viewExecution(planId: string): void {
    this.close();
    void this.router.navigate(['/app/audit-executions', planId]);
  }

  protected viewIssue(issue: IssueNotificationDto): void {
    this.close();
    const { location_name: _locationName, ...issueDto } = issue;
    this.issueRequested.emit(issueDto);
  }

  protected async submit(planId: string): Promise<void> {
    const reason = this.reason().trim();
    if (!reason || this.busyId()) return;
    this.busyId.set(planId);
    this.error.set('');
    try {
      await firstValueFrom(this.api.requestAuditUnlock(planId, reason));
      this.selectedPlanId.set('');
      this.reason.set('');
      await this.load();
    } catch (error) {
      const code = error instanceof HttpErrorResponse ? error.error?.code : '';
      this.error.set(code === 'unlock_request_pending'
        ? 'auditUnlock.errors.pending'
        : code === 'unlock_recipient_missing'
          ? 'auditUnlock.errors.recipientMissing'
          : 'auditUnlock.errors.request');
    } finally {
      this.busyId.set('');
    }
  }

  protected async decide(requestId: string, decision: 'approve' | 'reject'): Promise<void> {
    if (this.busyId()) return;
    this.busyId.set(requestId);
    this.error.set('');
    try {
      await firstValueFrom(this.api.decideAuditUnlock(requestId, decision));
      await this.load();
    } catch {
      this.error.set('auditUnlock.errors.decision');
    } finally {
      this.busyId.set('');
    }
  }

  protected date(value: string): string {
    return new Intl.DateTimeFormat(this.i18n.language() === 'pl' ? 'pl-PL' : 'en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      this.inbox.set(await firstValueFrom(this.api.getAuditUnlockInbox()));
    } catch {
      this.error.set('auditUnlock.errors.load');
    } finally {
      this.loading.set(false);
    }
  }
}
